import { FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { differenceInCalendarDays } from 'date-fns';
import * as moment from 'moment';

export class ValidationService {
  private static readonly msgConfig = {
    required: 'general.errors.validation.required',
    invalidCreditCard: 'general.errors.validation.credit_card',
    email: 'general.errors.validation.email',
    invalidPassword: 'general.errors.validation.password',
    minlength: 'general.errors.validation.length',
    telefone: 'general.errors.validation.phone_number',
    date: 'general.errors.validation.date',
    cpf: 'general.errors.validation.cpf',
    cnpj: 'general.errors.validation.cnpj',
    cep: 'general.errors.validation.cep',
    pis: 'general.errors.validation.pis',
    year: 'general.errors.validation.year',
    value: 'general.errors.validation.value',
    mustMatch: 'general.errors.validation.must_match',
    pattern: 'general.errors.validation.pattern',
    numeroConta: 'general.errors.validation.account'
  };

  private static getValue(control) {
    if (!control || !control.value) {
      return '';
    }
    if (control.value.value) {
      return control.value.value;
    }
    return control.value;
  }

  private static validateRegex(control, regex, validation) {
    const val = ValidationService.getValue(control);
    if (!val) {
      return null;
    }
    if (val.toString().match(regex)) {
      return null;
    }

    const res = {};
    res[validation] = true;
    return res;
  }

  static getValidatorErrorMessage(validatorName: string, error: any, translate: TranslateService) {
    if (validatorName === 'mustMatch') {
      error.fieldname = translate.instant(error.field);
    }
    const msg = ValidationService.msgConfig[validatorName];
    return translate.instant(msg, error);
  }

  static creditCard(control) {
    // Visa, MasterCard, American Express, Diners Club, Discover, JCB
    if (
      control.value.match(
        /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/,
      )
    ) {
      return null;
    } else {
      return { invalidCreditCard: true };
    }
  }

  static email(control) {
    const r = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    return ValidationService.validateRegex(control, r, 'email');
  }

  static telefone(control) {
    const r = /^\d{2}\d{2}?\s*\d{4,5}?\d{4}$/;
    //const r = /^\+\d{2,}\(\d{2}\)?\s*\d{4,5}\-?\d{4}$/ //Regex com mascara
    return ValidationService.validateRegex(control, r, 'telefone');
  }

  public static cpf(control) {
    return ValidationService.validateCpf(control) ? null : { cpf: true };
  }

  public static date(control, disableFutureDate = false, disablePastDate = false) {
    const val = ValidationService.getValue(control);
    if (!val) {
      return null;
    }

    const regex = /^(((0[1-9]|[12]\d|3[01])\/(0[13578]|1[02])\/((19|[2-9]\d)\d{2}))|((0[1-9]|[12]\d|30)\/(0[13456789]|1[012])\/((19|[2-9]\d)\d{2}))|((0[1-9]|1\d|2[0-8])\/02\/((19|[2-9]\d)\d{2}))|(29\/02\/((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/g;
    const regexValidation = ValidationService.validateRegex(control, regex, 'date');

    const controlDate = moment(control.value, 'DD/MM/YYYY').format('YYYY-MM-DD');

    const isFuture = disableFutureDate && differenceInCalendarDays(controlDate, new Date()) > 0;
    const isPast = disablePastDate && differenceInCalendarDays(controlDate, new Date()) < 0;

    if ((!regexValidation && (isFuture || isPast)) || controlDate < '1900-12-31' || controlDate > '2079-12-31') {
      return { date: true };
    }

    return regexValidation;
  }

  public static cep(control) {
    return ValidationService.validateRegex(control, /^\d{5}\s*\d{3}$/, 'cep');
  }

  public static pis(control) {
    return ValidationService.validateRegex(control, /^\d{3}\s*\d{5}\s*\d{2}\s*\d{1}$/, 'pis');
  }

  public static cnpj(control) {
    return ValidationService.validateRegex(control, /^[0-9]{2}\.?[0-9]{3}\.?[0-9]{3}\/?[0-9]{4}\-?[0-9]{2}$/, 'cnpj');
  }

  public static custom(control, regex) {
    return ValidationService.validateRegex(control, regex, 'regex');
  }

  public static setFormValueNotRequired(controlName: string, control: any) {
    if (control[controlName]) {
      control[controlName].setValue(null)
      control[controlName].clearValidators();
      control[controlName].updateValueAndValidity();
    }
  }
  public static setFormValueRequired(controlName: string, control: any) {
    if (control[controlName]) {
      control[controlName].setValidators([Validators.required]);
      control[controlName].updateValueAndValidity();
    }
  }


  static password(control) {
    // {6,100}           - Assert password is between 6 and 100 characters
    // (?=.*[0-9])       - Assert a string has at least one number
    if (control.value.match(/^(?=.*[0-9])[a-zA-Z0-9!@#$%^&*]{6,100}$/)) {
      return null;
    } else {
      return { invalidPassword: true };
    }
  }

  static mustMatch(controlName: string, matchingControlName: string, fieldName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: { field: fieldName } });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  static validateCpf(control) {
    const cpf = ValidationService.getValue(control).toString().replace(/\D/g, '');
    // tslint:disable-next-line: one-variable-per-declaration
    var numeros, digitos, soma, i, resultado, digitos_iguais;
    digitos_iguais = 1;
    if (cpf.length < 11) return false;
    for (i = 0; i < cpf.length - 1; i++)
      if (cpf.charAt(i) != cpf.charAt(i + 1)) {
        digitos_iguais = 0;
        break;
      }
    if (!digitos_iguais) {
      numeros = cpf.substring(0, 9);
      digitos = cpf.substring(9);
      soma = 0;
      for (i = 10; i > 1; i--) soma += numeros.charAt(10 - i) * i;
      resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
      if (resultado != digitos.charAt(0)) return false;
      numeros = cpf.substring(0, 10);
      soma = 0;
      for (i = 11; i > 1; i--) soma += numeros.charAt(11 - i) * i;
      resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
      if (resultado != digitos.charAt(1)) return false;
      return true;
    } else return false;
  }
}
